import { CreatorsLinkList } from '@/General/CreatorsLinkList'
import { Price } from '@/General/Price'
import Link from 'next/link'
import { ButtonRow } from './ButtonRow'
import { ProductThumbnail } from '@/General/ProductThumbnail'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'

export const StandardProductListItem = (
  props: StandardProductListItemProps
) => {
  return (
    <div
      className="my-4 pb-4 border-b border-gray-200 w-full"
      key={props.id}
      data-product-row
    >
      <div className="flex flex-row space-x-4">
        {/* Thumbnail */}
        <div className="flex-shrink-0 md:block">
          <Link href={props.url || '#'} prefetch={false} data-product-link>
            <ProductThumbnail
              src={props.thumbnail ? `${props.thumbnail}?w=128&dpr=2` : null}
              alt={`${props.name} ${props.productType}`}
              className="w-[52px] h-[86px] md:w-[128px] md:h-[167px] object-cover rounded"
              loading="lazy"
            />
          </Link>
          {props.adminImageEditorUrl && (
            <a
              href={props.adminImageEditorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex w-[52px] items-center justify-center gap-1 rounded border border-gray-300 px-1 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vi-red md:w-[128px] md:px-2"
              aria-label={`Edit images for ${props.name} in a new tab`}
            >
              <span>Edit image</span>
              <ArrowTopRightOnSquareIcon
                className="hidden size-3.5 md:block"
                aria-hidden="true"
              />
            </a>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header with VI Exclusive icon and title */}
          <div className="flex items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <Link
                  href={props.url || '#'}
                  prefetch={false}
                  data-product-link
                  className="text-vi-red hover:underline font-bold text-xl md:text-2xl leading-tight line-clamp-2 flex-1 min-w-0"
                >
                  {props.name}
                </Link>
                {/* Mobile VI Exclusive icon - right side */}
                {props.vanishingIncExclusive && (
                  <Link
                    href="/exclusives/"
                    prefetch={false}
                    className="ml-2 flex-shrink-0"
                  >
                    <img
                      alt="VI Exclusive"
                      src="https://vinc.gumlet.io/pics/icons/vi-exclusive-mobile-1.png?w=20"
                      className="md:hidden"
                    />
                    <img
                      src="https://vinc.gumlet.io/pics/icons/vi-exclusive-1.png?w=100&dpr=2"
                      alt="VI Exclusive"
                      className="hidden md:block w-24 mr-4"
                    />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Product type and creators */}
          <div className="text-gray-500 text-base mb-2">
            {props.productType}
            {(props.creators?.length ?? 0) > 0 && (
              <>
                {' '}
                by <CreatorsLinkList creators={props.creators} />
              </>
            )}
            {' - '}
            <Price
              currencySymbol={props.currencySymbol}
              currencyCode={props.currencyCode}
              price={props.price ?? 0}
              salePrice={props.salePrice}
              priceIncVat={props.options[0]?.priceIncVat}
              salePriceIncVat={props.options[0]?.salePriceIncVat}
              taxType={props.options[0]?.taxType}
              options={
                props.options.length > 1
                  ? props.options.map(opt => ({
                      price: opt.price,
                      salePrice: opt.salePrice,
                      priceIncVat: opt.priceIncVat,
                      salePriceIncVat: opt.salePriceIncVat,
                      taxType: opt.taxType,
                      productType: opt.productType,
                    }))
                  : undefined
              }
            />
          </div>

          {/* Additional info */}
          {props.additionalInfo && (
            <div className="text-gray-500 text-base mb-2">
              {props.additionalInfo}
            </div>
          )}

          {/* Description */}
          {props.blurb && (
            <div className="text-gray-600 text-base line-clamp-2 md:line-clamp-3">
              <span
                dangerouslySetInnerHTML={{ __html: props.blurb + '...' || '' }}
              />
            </div>
          )}

          <ButtonRow
            productId={props.id}
            url={props.url}
            name={props.name}
            productType={props.productType}
            options={props.options}
            isFreeDownload={props.isFreeDownload}
            productSlug={props.productSlug}
            hasDownloads={props.hasDownloads}
            primaryAction={props.primaryAction}
          />
        </div>
      </div>
    </div>
  )
}

interface StandardProductListItemProps {
  id: number
  adminImageEditorUrl?: string
  thumbnail?: string | null
  name: string
  blurb: string
  url: string
  options: {
    id: number
    description: string
    price: number
    salePrice: number
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    currencySymbol: string
    inStock: boolean
    productType: string
    needsShipping: boolean
    taxType?: number | null
  }[]
  vanishingIncExclusive: boolean
  isFreeDownload?: boolean
  productSlug?: string
  hasDownloads?: boolean
  price?: number | null
  currencySymbol: string
  currencyCode?: string | null
  productType: string
  creators?:
    | ({
        urlStub?: string | null
        name?: string | null
      } | null)[]
    | null
    | undefined
  salePrice?: number | null
  additionalInfo?: React.ReactNode
  additionalButtons?: React.ReactNode
  primaryAction?: 'more-info' | 'add-to-cart'
}
